/**
 * ImportContactsToAccountDialog
 *
 * Seeds a chosen account with every Respectly tenant as a contact (one per
 * account owner) so it can be used as a marketing account to reach the platform's
 * own customers. Opened two ways:
 *   - from the Accounts page header with no target → operator picks the account;
 *   - from an account row's ⋯ menu → target is pre-selected and locked.
 *
 * The one deliberately expressive element is the live import meter; everything
 * else stays quiet and matches AssignToAccountsDialog.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Search,
  Users,
  X,
  Check,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  DownloadCloud,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
  useToast,
} from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import { useAuth } from '@respectly/utils/auth';
import type { AccountListItem } from '@respectly/shared-types';
import {
  importAllTenantsToAccount,
  type ImportProgressEvent,
  type ImportResult,
  type ImportSummary,
} from '../../lib/importContactsToAccount';

interface PickerAccount {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, the target is fixed (opened from a row) and the picker is hidden. */
  preselectedAccount?: AccountListItem | null;
  /** Called after a successful import so the caller can refresh the account list. */
  onImported?: () => void;
}

type Phase = 'configure' | 'running' | 'done';

const ZERO_PROGRESS: ImportProgressEvent = {
  processed: 0,
  totalRows: 0,
  created: 0,
  updated: 0,
  skipped: 0,
  failed: 0,
};

function humanizeError(result: Extract<ImportResult, { ok: false }>): string {
  if (result.error.code === 'LIMIT_EXCEEDED') {
    return "This account's plan can't hold any more contacts. Move it to a higher plan and try again.";
  }
  if (result.status === 404) return 'That account no longer exists. Refresh and try again.';
  return result.error.message || 'The import failed. Please try again.';
}

export function ImportContactsToAccountDialog({
  open,
  onClose,
  preselectedAccount,
  onImported,
}: Props) {
  const { getAccessToken } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState<Phase>('configure');
  const [accounts, setAccounts] = useState<PickerAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [progress, setProgress] = useState<ImportProgressEvent>(ZERO_PROGRESS);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const locked = !!preselectedAccount;

  // Reset everything each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setPhase('configure');
    setSearch('');
    setSelectedId(preselectedAccount?.id ?? null);
    setIncludeSuspended(false);
    setProgress(ZERO_PROGRESS);
    setSummary(null);
    setErrorMessage(null);
  }, [open, preselectedAccount]);

  // Fetch the account list only when we need the picker.
  useEffect(() => {
    if (!open || locked) return;
    let cancelled = false;
    (async () => {
      setIsLoadingAccounts(true);
      try {
        const api = getApiClient();
        const params = new URLSearchParams({ page: '1', limit: '500' });
        const result = await api.get<{ data: PickerAccount[] }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?${params.toString()}`
        );
        if (!cancelled) setAccounts(result.data ?? []);
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setIsLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, locked]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) =>
      q
        ? a.name.toLowerCase().includes(q) ||
          a.slug.toLowerCase().includes(q) ||
          (a.ownerEmail?.toLowerCase().includes(q) ?? false)
        : true
    );
  }, [accounts, search]);

  const targetName =
    preselectedAccount?.name ?? accounts.find((a) => a.id === selectedId)?.name ?? null;

  const pct =
    progress.totalRows > 0 ? Math.round((progress.processed / progress.totalRows) * 100) : 0;

  const handleRun = async () => {
    if (!selectedId) return;
    setPhase('running');
    setProgress(ZERO_PROGRESS);
    setErrorMessage(null);
    try {
      const result = await importAllTenantsToAccount({
        tenantId: selectedId,
        getToken: getAccessToken,
        includeSuspended,
        onProgress: (e) => setProgress(e),
      });
      if (result.ok) {
        setSummary(result.data.summary);
        setPhase('done');
        onImported?.();
        toast({
          title: `Imported ${result.data.summary.created.toLocaleString()} contact${result.data.summary.created === 1 ? '' : 's'}`,
          description: `${targetName ?? 'The account'} is ready for outreach.`,
        });
      } else {
        setErrorMessage(humanizeError(result));
        setPhase('configure');
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'The import failed. Please try again.');
      setPhase('configure');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && phase !== 'running' && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-500" />
            Import contacts to an account
          </DialogTitle>
        </DialogHeader>

        {/* What this does */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Adds every Respectly account as a contact — one per account owner (name, email, and
            phone where available) — so this account can reach the platform's customers.
          </p>
          <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
            <span>
              Contacts are added without marketing consent — outreach still follows the usual
              consent rules.
            </span>
          </div>
        </div>

        {/* Target account */}
        {locked ? (
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Target account
            </p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-900 dark:text-white">
                  {preselectedAccount?.name}
                </div>
                <div className="truncate text-xs text-slate-500">{preselectedAccount?.slug}</div>
              </div>
              <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-xs tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {(preselectedAccount?.contactCount ?? 0).toLocaleString()} contacts
              </span>
            </div>
          </div>
        ) : (
          phase === 'configure' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search accounts by name, slug, or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="max-h-[240px] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800">
                {isLoadingAccounts ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : filteredAccounts.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">
                    No matching accounts.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredAccounts.map((acct) => {
                      const checked = selectedId === acct.id;
                      return (
                        <li
                          key={acct.id}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                            checked
                              ? 'bg-sky-50/60 dark:bg-sky-950/20'
                              : 'hover:bg-slate-50/60 dark:hover:bg-slate-900/40'
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedId(acct.id)}
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                              checked
                                ? 'border-sky-500 bg-sky-500 text-white'
                                : 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900'
                            )}
                            aria-label={`Select ${acct.name}`}
                          >
                            {checked && <Check className="h-3 w-3" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-slate-900 dark:text-white">
                              {acct.name}
                            </div>
                            <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-500">
                              <span className="truncate">{acct.slug}</span>
                              {acct.ownerEmail && (
                                <>
                                  <span className="text-slate-300 dark:text-slate-600">·</span>
                                  <span className="truncate font-mono text-[11px]">
                                    {acct.ownerEmail}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )
        )}

        {/* Options + error (configure only) */}
        {phase === 'configure' && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={includeSuspended}
                onChange={(e) => setIncludeSuspended(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500 dark:border-slate-600"
              />
              Include suspended and cancelled accounts
            </label>
            {errorMessage && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        )}

        {/* Signature moment — live import meter */}
        {phase === 'running' && (
          <div className="space-y-3 py-1">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Importing into {targetName ?? 'account'}…
              </span>
              <span className="font-mono text-sm tabular-nums text-slate-500">
                {progress.processed.toLocaleString()} / {progress.totalRows.toLocaleString()}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-[width] duration-300 ease-out"
                style={{ width: `${Math.max(pct, progress.totalRows === 0 ? 0 : 4)}%` }}
              />
            </div>
            <div className="grid grid-cols-4 gap-2 pt-1">
              <MeterStat label="Created" value={progress.created} tone="emerald" />
              <MeterStat label="Updated" value={progress.updated} tone="sky" />
              <MeterStat label="Skipped" value={progress.skipped} tone="slate" />
              <MeterStat label="Failed" value={progress.failed} tone="red" />
            </div>
          </div>
        )}

        {/* Result */}
        {phase === 'done' && summary && (
          <div className="space-y-3 py-1">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              Imported {summary.created.toLocaleString()} contact{summary.created === 1 ? '' : 's'}
              {targetName ? ` into ${targetName}` : ''}.
            </div>
            <div className="grid grid-cols-4 gap-2">
              <MeterStat label="Created" value={summary.created} tone="emerald" />
              <MeterStat label="Updated" value={summary.updated} tone="sky" />
              <MeterStat label="Skipped" value={summary.skipped} tone="slate" />
              <MeterStat label="Failed" value={summary.failed} tone="red" />
            </div>
            {summary.skipped > 0 && (
              <p className="text-xs text-slate-500">
                Skipped entries were already contacts on this account (matched by email or phone).
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'done' ? (
            <Button
              onClick={onClose}
              className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={phase === 'running'}>
                <X className="mr-1.5 h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={handleRun}
                disabled={!selectedId || phase === 'running'}
                className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                {phase === 'running' ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <DownloadCloud className="mr-1.5 h-4 w-4" />
                )}
                Import contacts
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeterStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'emerald' | 'sky' | 'slate' | 'red';
}) {
  const toneClass = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    sky: 'text-sky-600 dark:text-sky-400',
    slate: 'text-slate-600 dark:text-slate-300',
    red: 'text-red-600 dark:text-red-400',
  }[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-center dark:border-slate-800 dark:bg-slate-900">
      <div className={cn('font-mono text-lg font-semibold tabular-nums', toneClass)}>
        {value.toLocaleString()}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </div>
    </div>
  );
}
